import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { showErrorThunk } from '@spike/notifications-action';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import SearchBar from 'components/UI/V3/SearchBar/SearchBar';
import Spinner from 'components/UI/V3/Spinner';
import { useAuth, useSettingsMarketplace } from 'hooks';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';
import { FunctionComponent, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { BusinessSettingsTitle } from '../../UI';
import { fetchIdogcamViewerLaunch } from './api/IdogcamApi';
import IdogcamCameraDrawer from './IdogcamCameraDrawer/IdogcamCameraDrawer';
import IdogcamCamerasTable from './IdogcamCamerasTable/IdogcamCamerasTable';
import IdogcamConnectForm, { IdogcamConnectFormLocation } from './IdogcamConnectForm/IdogcamConnectForm';
import IdogcamLocationFilter from './IdogcamLocationFilter/IdogcamLocationFilter';
import useIdogcamCameras from './hooks/useIdogcamCameras';
import { IdogcamPopupBlockedError, openIdogcamViewer } from './lib/openIdogcamViewer';
import { IdogcamCamera, IDOGCAM_ICON_SRC } from './model/IdogcamModel';
import styles from './Idogcam.module.scss';

export interface Props {
    className?: string;
    onBack: () => void;
}

const Idogcam: FunctionComponent<Props> = ({ className, onBack }) => {
    const auth = useAuth();
    const settingsMarketplace = useSettingsMarketplace();
    const dispatch = useDispatch();

    // Location = marketplace of the organization. Each one has its own iDogCam account,
    // so the screen works on one location at a time (marketplace_id sent to the backend).
    const [marketplaceId, setMarketplaceId] = useState<number>(settingsMarketplace.id ?? auth.marketplaceId!);
    const [showConnectionSettings, setShowConnectionSettings] = useState(false);
    const [editingCamera, setEditingCamera] = useState<IdogcamCamera | null>(null);

    const camerasState = useIdogcamCameras(marketplaceId);
    const { getOrganizationMarketplace, organizationMarketplaces } = useOrganizationMarketplaces();

    const locationName = getOrganizationMarketplace(marketplaceId)?.name;
    const locations: Array<IdogcamConnectFormLocation> = useMemo(
        () =>
            organizationMarketplaces
                .map(location => ({ id: location.id, name: location.name }))
                .sort((location, otherLocation) => location.name.localeCompare(otherLocation.name)),
        [organizationMarketplaces]
    );

    // Two views: the connection form (location not connected yet, or opened from "Connection settings")
    // and the camera list.
    const showConnectionForm = !camerasState.isLoadingLocation && (!camerasState.configured || showConnectionSettings);
    const showCameras = !camerasState.isLoadingLocation && camerasState.configured && !showConnectionSettings;
    // The empty state ("No cameras found") has its own Connection settings button.
    const showHeaderConnectionSettings =
        showCameras && (camerasState.cameras.length > 0 || camerasState.searchText.trim().length > 0);

    const changeLocationHandler = (selectedMarketplaceId: number) => {
        if (selectedMarketplaceId !== marketplaceId) {
            setMarketplaceId(selectedMarketplaceId);
        }
    };

    // The form may connect any location; afterwards the screen shows that location's cameras.
    const connectedHandler = (connectedMarketplaceId: number) => {
        setShowConnectionSettings(false);
        if (connectedMarketplaceId === marketplaceId) {
            camerasState.applyConnection();
        } else {
            camerasState.skipNextSync();
            setMarketplaceId(connectedMarketplaceId);
        }
    };

    const cameraSavedHandler = (camera: IdogcamCamera) => {
        camerasState.replaceCamera(camera);
        setEditingCamera(null);
    };

    // Staff preview of the live feed, same launch flow pet parents use in PPP.
    const viewCameraHandler = (camera: IdogcamCamera) => {
        openIdogcamViewer(() => fetchIdogcamViewerLaunch(marketplaceId, auth.token!, camera.id)).catch(error => {
            dispatch(
                showErrorThunk(
                    error instanceof IdogcamPopupBlockedError
                        ? 'Your browser blocked the camera window. Allow pop-ups for Cuddles and try again.'
                        : "We couldn't open this camera. Please try again."
                )
            );
        });
    };

    return (
        <div className={clsx(styles.container, className)}>
            <div className={styles.titleContainer}>
                <BusinessSettingsTitle className={styles.businessSettingsTitle}>
                    <FontAwesomeIcon
                        icon={faArrowLeft}
                        className={styles.backIcon}
                        onClick={onBack}
                    />
                    <img
                        src={IDOGCAM_ICON_SRC}
                        alt="iDogCam logo"
                        className={styles.integrationIcon}
                    />
                    iDogCam
                </BusinessSettingsTitle>
                {showHeaderConnectionSettings && (
                    <Button
                        label="Connection settings"
                        variant="black"
                        size="medium"
                        onClick={() => setShowConnectionSettings(true)}
                    />
                )}
            </div>

            {camerasState.isLoadingLocation && (
                <div className={styles.spinnerContainer}>
                    <Spinner />
                </div>
            )}

            {showConnectionForm && (
                <IdogcamConnectForm
                    key={`connection-${marketplaceId}`}
                    locations={locations}
                    initialLocationId={marketplaceId}
                    onConnected={connectedHandler}
                    // Only when this location already has a camera list to go back to.
                    onCancel={camerasState.configured ? () => setShowConnectionSettings(false) : undefined}
                />
            )}

            {showCameras && (
                <>
                    <SearchBar
                        placeholder="Search by camera name"
                        searchText={camerasState.searchText}
                        onChangeSearchText={camerasState.setSearchText}
                        rightActions={
                            <IdogcamLocationFilter
                                locations={locations}
                                selectedLocationId={marketplaceId}
                                onChange={changeLocationHandler}
                            />
                        }
                    />
                    <IdogcamCamerasTable
                        cameras={camerasState.cameras}
                        locationName={locationName}
                        page={camerasState.page}
                        pageSize={camerasState.pageSize}
                        totalPages={camerasState.totalPages}
                        isSearching={camerasState.searchText.trim().length > 0}
                        isLoading={camerasState.isLoadingPage}
                        className={styles.table}
                        onPageChange={camerasState.setPage}
                        onPageSizeChange={camerasState.setPageSize}
                        onConnectionSettings={() => setShowConnectionSettings(true)}
                        onEdit={setEditingCamera}
                        onView={viewCameraHandler}
                    />
                </>
            )}

            {editingCamera && (
                <IdogcamCameraDrawer
                    camera={editingCamera}
                    marketplaceId={marketplaceId}
                    locations={camerasState.locations}
                    onClose={() => setEditingCamera(null)}
                    onSaved={cameraSavedHandler}
                />
            )}
        </div>
    );
};

export default Idogcam;
